package dev.lingvo.site.components.studio.editor

import dev.lingvo.shared.ScreenshotPlanDto
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign

/** Mode belongs to the Design route; it does not add another product destination. */
internal enum class DesignMode(val id: String) {
    LAYOUT("layout"), CANVAS("canvas");
    companion object { fun parse(raw: String?): DesignMode = entries.firstOrNull { it.id == raw } ?: LAYOUT }
}

/** URL slots are plan IDs. The document and renderer use stable slot keys. */
internal data class CanvasLocation(val view: HistoryView, val slotId: String, val layer: String?)

internal fun canvasLocation(
    plan: ScreenshotPlanDto, design: ScreenshotDesign, slotId: String?, device: String?, locale: String?,
    layer: String?, locales: List<String>,
): CanvasLocation? {
    val slots = plan.slots.sortedBy { it.position }
    val requested = slots.firstOrNull { it.id == slotId }
    val board = design.boards.firstOrNull { it.deviceClass == device && it.pages.isNotEmpty() }
        ?: design.boards.firstOrNull { board -> requested != null && board.pages.any { requested.key in it.slotIds } }
        ?: design.boards.firstOrNull { it.pages.isNotEmpty() } ?: return null
    val slot = requested?.takeIf { wanted -> board.pages.any { wanted.key in it.slotIds } }
        ?: slots.firstOrNull { candidate -> board.pages.any { candidate.key in it.slotIds } } ?: return null
    val page = board.pages.first { slot.key in it.slotIds }
    fun ids(items: List<Layer>): Set<String> = items.flatMap {
        listOf(it.base.id) + if (it is GroupLayer) ids(it.children) else emptySet()
    }.toSet()
    val selected = layer?.takeIf { it in ids(page.layers) }
    return CanvasLocation(HistoryView(slot.key, locale?.takeIf { it in locales } ?: locales.first(), board.deviceClass,
        setOfNotNull(selected)), slot.id, selected)
}

/** Caption work is a separate guarded write and must be settled before retiring its editor. */
internal fun captionPending(caption: CaptionSnapshot?): Boolean = caption?.let { it.saving || it.draft != it.value } == true

/** Panorama changes page structure; ordinary scene commands only know the selected page. */
internal fun canvasSceneCommand(
    state: StudioUiState, scene: dev.lingvo.shared.screenshots.doc.SceneSpec,
    arranger: dev.lingvo.shared.screenshots.scene.SceneArranger,
    input: dev.lingvo.shared.screenshots.scene.SceneInput,
): EditCommand = EditCommand { document ->
    val board = document.boards.first { it.id == state.board.id }
    val pages = if (scene.kind == dev.lingvo.shared.screenshots.doc.SceneKind.PANORAMA) {
        val singles = board.pages.flatMap { if (it.slotIds.size > 1) arranger.splitPanorama(it, board.canvas) else listOf(it) }
        val joined = if (singles.size > 1) arranger.toPanorama(singles, board.canvas) else singles.single()
        listOf(arranger.apply(joined, input.copy(slots = joined.slotIds.size, spec = scene)))
    } else board.pages.flatMap { page ->
        if (page.id != state.page.id) listOf(page)
        else (if (page.slotIds.size > 1) arranger.splitPanorama(page, board.canvas) else listOf(page))
            .map { arranger.apply(it, input.copy(slots = 1, spec = scene)) }
    }
    val next = document.copy(boards = document.boards.map { if (it.id == board.id) it.copy(pages = pages) else it })
    when (val decoded = dev.lingvo.shared.screenshots.doc.DesignCodec.decode(
        dev.lingvo.shared.screenshots.doc.DesignCodec.encode(next), dev.lingvo.shared.screenshots.doc.StoreDeviceClassRule)) {
        is dev.lingvo.shared.screenshots.doc.DecodeResult.Ok -> decoded.design
        is dev.lingvo.shared.screenshots.doc.DecodeResult.Refused -> throw IllegalArgumentException("This scene cannot be applied to the current pages")
    }
}
